import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Button, ButtonLink, LoadingTable } from '../../components/base';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { DataTable, IDataTableColumn } from '../../components/common/DataTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../components/container';
import { BaseShowDetail } from '../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../context/base/eBaseActionStatus';
import { actions } from '../../context/stores/rootActions';
import { AppState } from '../../context/stores/rootReducers';
import ProductTypeDetail from './ProductTypeDetail';
import { ProductTypeModel } from '../../context/models/product-type/ProductTypeModel';
import { ProductTypeActionTypes } from '../../context/stores/product-type/IProductTypeActionsTypes';
import { useNavigate } from 'react-router-dom';

type IProductTypePageProps = {
	titlePage: string;
	status: eBaseActionStatus;
	allProductTypes: ProductTypeModel[];
	selectedIds: number[];
	showDetail: BaseShowDetail<ProductTypeModel>;
	showConfirm: boolean;
	handleReloadAllData: () => void;
	handleSaveData: (data: ProductTypeModel) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onShowDetail: (data: BaseShowDetail<ProductTypeModel>) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const mapStateToProps = (state: AppState) => {
	return {
		titlePage: `loại sản phẩm`,
		status: state.productType.status,
		allProductTypes: state.productType.allProductTypes,
		selectedIds: state.productType.selectedIds,
		showDetail: state.productType.showDetail,
		showConfirm: state.productType.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<ProductTypeActionTypes>) => ({
	handleReloadAllData: () => {
		dispatch(actions.productType.getAllRequest());
	},
	handleSaveData: (data: ProductTypeModel) => {
		dispatch(actions.productType.saveRequest(data));
	},
	handleDeleteData: (ids: number[]) => {
		dispatch(actions.productType.deleteRequest(ids));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.productType.changeSelectedIds(ids));
	},
	onShowDetail: (data: BaseShowDetail<ProductTypeModel>) => {
		dispatch(actions.productType.showDetail(data));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.productType.showConfirm(isShow));
	},
});

const ProductTypePage = (props: IProductTypePageProps) => {
	const navigate = useNavigate();
	const {
		titlePage,
		status,
		selectedIds,
		showConfirm,
		showDetail,
		allProductTypes,
		handleReloadAllData,
		handleDeleteData,
		onChangeSelectedIds,
		handleSaveData,
		onShowDetail,
		onShowConfirm,
	} = props;

	allProductTypes.sort((a, b) => a.stt - b.stt);

	useEffect(() => {
		handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const columns = useMemo((): IDataTableColumn<ProductTypeModel>[] => {
		return [
			{ accessorKey: 'stt', header: 'Số thứ tự', size: 100, meta: { align: 'center' } },
			{ accessorKey: 'name', header: 'Tên', minSize: 150 },
			{ accessorKey: 'icon', header: 'Icon', size: 150 },
			{ accessorKey: 'url', header: 'Đường dẫn', size: 150 },
			{ accessorKey: 'background', header: 'Background', size: 150 },
			{
				id: 'actions',
				header: 'Sửa',
				meta: { align: 'center' },
				size: 150,
				cell: ({ row }) => {
					return row.original.id > 0 ? (
						<div className="flex gap-2 justify-center">
							<Button
								onClick={() => {
									localStorage.setItem('product_type_id', String(row.original.id));
									navigate(`/product-type-product`, { replace: true });
								}}
								text=''
								icon='ni ni-grid-box-alt-fill'
								preset='dimoutline'
								theme='info'
								title='Nhóm sản phẩm thuộc loại sản phẩm'
							/>
							<Button
								onClick={() => onShowDetail({ isShow: true, detailData: row.original })}
								text=''
								icon='ni ni-edit'
								preset='dimoutline'
								theme='primary'
								title='Sửa'
							/>
						</div>
					) : null;
				}
			}
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`}>
					<div className='flex flex-wrap items-center justify-end'>
						<ul className='flex flex-wrap items-center gap-2 [&>li]:list-none'>
							<li>
								<Button
									text={`Xóa (${selectedIds.length})`}
									icon='icon ni ni-trash'
									className='hidden md:inline-flex'
									theme='danger'
									isDisabled={selectedIds.length <= 0}
									onClick={() => onShowConfirm(true)}
								/>
							</li>
							<li>
								<Button
									text='Tạo mới'
									icon='icon ni ni-plus'
									theme='primary'
									className='hidden md:inline-flex'
									onClick={() => onShowDetail({ isShow: true })}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={allProductTypes}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]).map(Number));
								}}
								enablePagination
								totalRecords={allProductTypes.length}
								enableGlobalFilter
								toolbar={{
									isShowSearch: true,
									searchPlaceholder: `Tìm kiếm...`
								}}
								onRefresh={handleReloadAllData}
							/>
						)}
					</BlockCard>
				</PageBody>
			</PageContainer>
			{showDetail && showDetail.isShow && (
				<>
					<ProductTypeDetail
						isSaving={status === eBaseActionStatus.saving}
						isShow={showDetail.isShow}
						detailModel={showDetail.detailData}
						onClose={() => onShowDetail({ isShow: false })}
						onSubmit={(data) => handleSaveData(data)}
						title={`${showDetail.detailData ? `Cập nhật` : `Thêm mới`} ${titlePage}`}
					/>
				</>
			)}
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn xóa ${selectedIds.length} ${titlePage.toLowerCase()} đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleDeleteData(selectedIds)}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Xóa'
						btnCloseText='Không xóa'
					/>
				</>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductTypePage);
